/**
 * Parse a video URL into a normalized shape the guestbook + property landing
 * can render with a lightweight built-in player.
 *
 * ⚠️ Product rule (2026-02): hosts can ONLY paste links from YouTube / Vimeo /
 *    Google Drive. Direct file uploads are NOT allowed in the normal editor to
 *    keep Hostland storage costs down. The `mp4` kind is intentionally reserved
 *    for the internal demo property whose files ship in `public/videos/`.
 *
 * Returns null for unsupported URLs so callers can show a validation error.
 */
export function parseVideoUrl(url) {
  if (!url || typeof url !== "string") return null;
  const raw = url.trim();
  if (!raw) return null;

  // ─── YouTube ────────────────────────────────────────────────────────
  //  Supports watch?v=, youtu.be/, embed/, v/, shorts/, live/
  const yt = raw.match(
    /(?:youtube\.com\/(?:watch\?(?:.*?&)?v=|embed\/|v\/|shorts\/|live\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/
  );
  if (yt) {
    return {
      kind: "youtube",
      id: yt[1],
      embedUrl: `https://www.youtube-nocookie.com/embed/${yt[1]}?rel=0&modestbranding=1&playsinline=1`,
    };
  }

  // ─── Vimeo ──────────────────────────────────────────────────────────
  //  https://vimeo.com/123456789  or  https://player.vimeo.com/video/123456789
  const vim = raw.match(/vimeo\.com\/(?:video\/|channels\/[^/]+\/|groups\/[^/]+\/videos\/)?([0-9]{6,})/);
  if (vim) {
    return {
      kind: "vimeo",
      id: vim[1],
      embedUrl: `https://player.vimeo.com/video/${vim[1]}?title=0&byline=0&portrait=0`,
    };
  }

  // ─── Google Drive ───────────────────────────────────────────────────
  //  https://drive.google.com/file/d/FILE_ID/view?usp=sharing
  //  https://drive.google.com/open?id=FILE_ID
  const drive =
    raw.match(/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/) ||
    raw.match(/drive\.google\.com\/open\?id=([a-zA-Z0-9_-]+)/) ||
    raw.match(/drive\.google\.com\/uc\?(?:.*?&)?id=([a-zA-Z0-9_-]+)/);
  if (drive) {
    return {
      kind: "drive",
      id: drive[1],
      embedUrl: `https://drive.google.com/file/d/${drive[1]}/preview`,
    };
  }

  // ─── Direct MP4 (demo only) ─────────────────────────────────────────
  if (/\.(mp4|webm|mov|m4v)(\?|$)/i.test(raw)) {
    return { kind: "mp4", id: raw, embedUrl: raw };
  }

  return null;
}

/**
 * Supported URL formats — user-facing help text.
 */
export const VIDEO_URL_HINT =
  "YouTube (anche 'non in elenco'), Vimeo o Google Drive (file impostato su 'chiunque con il link'). Non carichiamo file per non consumare storage.";
